import { Component, Input, Output, EventEmitter, ViewEncapsulation, OnChanges, SimpleChanges } from '@angular/core';
import { CommonDropdownData } from 'src/app/components/common/common-dropdown/common-dropdown.component.model';
import { CommonRadioData } from 'src/app/components/common/common-radio/common-radio.component.model';

@Component({
  selector: 'app-schedule-inspection',
  standalone: false,
  templateUrl: './schedule-inspection.component.html',
  styleUrls: ['./schedule-inspection.component.scss'],
  encapsulation: ViewEncapsulation.None 
})
export class ScheduleInspectionComponent implements OnChanges {
  
  private _activity: any;

  @Input()
  set activity(value: any) {
    this._activity = value;
    if (value) {
      this.initDynamicForm();
    }
  }
  
  get activity(): any {
    return this._activity;
  }

  @Input() clientId?: string; 

  @Output() confirm = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  dynamicFields: any[] = [];
  formValues: { [key: string]: any } = {};
  
  dropdownDataMap: { [key: string]: CommonDropdownData } = {};
  radioDataMap: { [key: string]: CommonRadioData } = {};

  selectedDateMap: { [key: string]: Date | null } = {};
  timeStateMap: { [key: string]: { hour: string, minute: string, period: 'AM'|'PM' } } = {};

  hours: string[] = Array.from({length: 12}, (_, i) => (i + 1).toString().padStart(2, '0'));
  minutes: string[] = Array.from({length: 12}, (_, i) => (i * 5).toString().padStart(2, '0'));
  periods: ('AM' | 'PM')[] = ['AM', 'PM'];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activity'] && changes['activity'].currentValue) {
      this.initDynamicForm();
    }
  }

  private getEasternOffsetMs(dateVal: any): number {
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return -(5 * 3600000);
      const utc = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' }));
      const loc = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      return loc.getTime() - utc.getTime();
    } catch (e) {
      return -(5 * 3600000);
    }
  }

  initDynamicForm() {
    this.dynamicFields = [];
    this.formValues = {};
    this.dropdownDataMap = {};
    this.radioDataMap = {};

    let layoutModel = this.activity?.model?.formLayoutModel;
    
    if (this.activity?.type === 'consolidated-inspection-activity') {
      layoutModel = this.activity.model?.scheduleStepActivity?.model?.formLayoutModel;
    }

    if (!layoutModel?.columnEntities) return;

    const entities = [...layoutModel.columnEntities].sort(
      (a: any, b: any) => a.displayOrder - b.displayOrder
    );

    this.dynamicFields = entities.map((col: any) => {
      const entity = col.entity;
      if (!entity) return null;

      if (entity.dataEntityTypeCode === 'date') {
        let initDate = null;
        if (entity.value) {
          try {
            const parsed = JSON.parse(entity.value);
            if (parsed.Value && !parsed.Value.includes('0001-01-01')) {
              const tempDate = new Date(parsed.Value);
              const offsetMs = this.getEasternOffsetMs(tempDate);
              initDate = new Date(tempDate.getTime() + (tempDate.getTimezoneOffset() * 60000) + offsetMs);
            }
          } catch (e) {}
        }
        
        if (!initDate && (entity.templateCode === 'SCHEDULE_SDL_USER' || entity.label?.toLowerCase().includes('sched'))) {
           const fallbackDate = this.activity?.scheduledOn || this.activity?.model?.inspection?.scheduledOn;
           if (fallbackDate && !String(fallbackDate).includes('0001-01-01')) {
             const tempDate = new Date(fallbackDate);
             const offsetMs = this.getEasternOffsetMs(tempDate);
             initDate = new Date(tempDate.getTime() + (tempDate.getTimezoneOffset() * 60000) + offsetMs);
           }
        }
        
        this.selectedDateMap[entity.$id] = initDate;
        
        let h = '12', m = '00', p: 'AM'|'PM' = 'AM';
        if (initDate && entity.includeTime) {
          let hours = initDate.getHours();
          p = hours >= 12 ? 'PM' : 'AM';
          h = (hours % 12 || 12).toString().padStart(2, '0');
          m = initDate.getMinutes().toString().padStart(2, '0');
        }
        this.timeStateMap[entity.$id] = { hour: h, minute: m, period: p };
      } 
      else if (entity.dataEntityTypeCode === 'list-data') {
        const rawOptions = entity.listTextValues?.length ? entity.listTextValues : (entity.availableListItems || []);
        
        const options = rawOptions
          .filter((o: any) => o.visible !== false)
          .map((o: any) => ({ label: o.text, value: o.value }));

        let selectedVal = entity.selectedValue || entity.value || '';
        
        // Forcefully prioritize the root property as the single source of truth 
        // in case the dynamic form payload retains stale selection values after an edit
        if (entity.templateCode === 'INSP_SDL_USER') {
          const rootInspectorId = this.activity?.inspectorId || this.activity?.model?.inspection?.inspectorId;
          if (rootInspectorId) {
            selectedVal = rootInspectorId;
          }
        }
        
        if (entity.templateCode === 'ScheduleInspectionType') {
          const rootInspType = this.activity?.model?.inspection?.inspectionType;
          if (rootInspType) {
            selectedVal = rootInspType;
          }
        }

        if (entity.controlType === 'dropdown') {
          this.dropdownDataMap[entity.$id] = {
            placeholder: 'Select Inspector',
            options: options,
            selectedValue: selectedVal
          };
        } else {
          this.radioDataMap[entity.$id] = {
            direction: 'column',
            options: options,
            value: selectedVal
          };
        }
        this.formValues[entity.$id] = selectedVal;
      }
      else {
        this.formValues[entity.$id] = entity.value || '';
      }

      return entity;
    }).filter(e => e !== null);
  }

  onDropdownChange(entityId: string, value: any) {
    if (this.dropdownDataMap[entityId]) this.dropdownDataMap[entityId].selectedValue = value;
    this.formValues[entityId] = value;
  }

  onRadioChange(entityId: string, value: string) {
    if (this.radioDataMap[entityId]) this.radioDataMap[entityId].value = value;
    this.formValues[entityId] = value;
  }

  getFormattedDateTime(entityId: string, includeTime: boolean): string {
    const d = this.selectedDateMap[entityId];
    if (!d) return '';
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const y = d.getFullYear();
    
    if (!includeTime) return `${m}/${day}/${y}`;
    
    const t = this.timeStateMap[entityId];
    return `${m}/${day}/${y} ${t.hour}:${t.minute} ${t.period}`;
  }

  setTime(entityId: string, type: 'hour'|'minute'|'period', val: string) {
    this.timeStateMap[entityId][type] = val as any;
  }

  get isFormValid(): boolean {
    let layoutModel = this.activity?.model?.formLayoutModel;
    if (this.activity?.type === 'consolidated-inspection-activity') {
      layoutModel = this.activity.model?.scheduleStepActivity?.model?.formLayoutModel;
    }

    if (!layoutModel?.columnEntities) return true;

    for (const col of layoutModel.columnEntities) {
      const entity = col.entity;
      if (!entity || !entity.isRequired) continue;

      let hasValue = false;
      if (entity.dataEntityTypeCode === 'date') {
        hasValue = !!this.selectedDateMap[entity.$id];
      } else if (entity.dataEntityTypeCode === 'list-data') {
        hasValue = !!this.formValues[entity.$id];
      } else if (entity.dataEntityTypeCode === 'free-form-text') {
        hasValue = !!this.formValues[entity.$id] && String(this.formValues[entity.$id]).trim() !== '';
      } else {
        hasValue = !!this.formValues[entity.$id];
      }

      if (!hasValue) return false;
    }
    return true;
  }

  onSubmit() {
    let layoutModel = this.activity?.model?.formLayoutModel;
    if (this.activity?.type === 'consolidated-inspection-activity') {
      layoutModel = this.activity.model?.scheduleStepActivity?.model?.formLayoutModel;
    }

    if (layoutModel?.columnEntities) {
      layoutModel.columnEntities.forEach((col: any) => {
        const entity = col.entity;
        if (!entity) return;

        const val = this.formValues[entity.$id];

        if (entity.dataEntityTypeCode === 'date') {
          const selectedD = this.selectedDateMap[entity.$id];
          if (selectedD) {
            const finalD = new Date(selectedD);
            if (entity.includeTime) {
              const t = this.timeStateMap[entity.$id];
              let h = parseInt(t.hour, 10);
              if (t.period === 'PM' && h !== 12) h += 12;
              if (t.period === 'AM' && h === 12) h = 0;
              finalD.setHours(h, parseInt(t.minute, 10), 0);
            }
            
            const offsetMs = this.getEasternOffsetMs(finalD);
            const realUtcMs = finalD.getTime() - (finalD.getTimezoneOffset() * 60000) - offsetMs;
            const iso = new Date(realUtcMs).toISOString();
            
            entity.value = JSON.stringify({ Value: iso, HasTime: entity.includeTime, DSTFixed: true });
            entity.formattedValue = this.getFormattedDateTime(entity.$id, entity.includeTime);

            if (entity.templateCode === 'SCHEDULE_SDL_USER') {
              this.activity.scheduledOn = iso;
              this.activity.scheduledOnIncludesTime = entity.includeTime;
              if (this.activity.model?.inspection) {
                this.activity.model.inspection.scheduledOn = iso;
                this.activity.model.inspection.scheduledOnIncludesTime = entity.includeTime;
              }
            }
          } else {
             entity.value = "";
             entity.formattedValue = "";
          }
        } 
        else if (entity.dataEntityTypeCode === 'list-data') {
          entity.selectedValue = val;
          entity.value = val;

          if (entity.templateCode === 'INSP_SDL_USER') {
            const rawOptions = entity.listTextValues?.length ? entity.listTextValues : (entity.availableListItems || []);
            const listOpt = rawOptions.find((o: any) => o.value === val);
            const inspectorName = listOpt ? listOpt.text : '';

            this.activity.inspectorId = val;
            if (this.activity.model?.inspection) {
               this.activity.model.inspection.inspectorId = val;
               this.activity.model.inspection.inspectorName = inspectorName;
            }
          }
          if (entity.templateCode === 'ScheduleInspectionType') {
            if (this.activity.model?.inspection) {
               this.activity.model.inspection.inspectionType = val;
            }
          }
        } 
        else if (entity.dataEntityTypeCode === 'free-form-text') {
          entity.value = val;
        }
      });
    }

    this.prepareConsolidatedSubmittal(2);
    this.confirm.emit(this.activity);
  }

  private prepareConsolidatedSubmittal(submittedStep: number): void {
    if (this.activity?.type !== 'consolidated-inspection-activity') return;

    const requestColumns = this.activity.model?.requestStepActivity?.model?.formLayoutModel?.columnEntities ?? [];
    const scheduleColumns = this.activity.model?.scheduleStepActivity?.model?.formLayoutModel?.columnEntities ?? [];
    const completeColumns = this.activity.model?.completeStepActivity?.model?.formLayoutModel?.columnEntities ?? [];

    this.activity.submittedStep = submittedStep;
    if (this.activity.model?.formLayoutModel) {
      this.activity.model.formLayoutModel.columnEntities = [
        ...requestColumns,
        ...scheduleColumns,
        ...completeColumns
      ];
    }

    this.activity.model.requestStepActivity = null;
    this.activity.model.scheduleStepActivity = null;
    this.activity.model.completeStepActivity = null;
  }

  onCancel() { this.cancel.emit(); }
}